import { APIResponse } from '../models';
import { createAPIError } from '../utils/createAPIError';
import { parseResponse } from '../utils/parseResponse';
import { EnrollSourceApplicationIds } from '../auth/models/RegistrationRequestWithoutOrg';
import { CountryCode } from '../crm/organization/enrollment/models';

export interface Claim {
    type: string;
    value: string;
}

export interface UserRegistrationResponse {
    userId: string;
    result: UserRegistrationResult;
    token: string;
    refreshToken: string;
    inactivityTimeoutMinutes: number;
    oneTimeCode: string;
}

export type UserRegistrationResult =
    | 'Unknown'
    | 'Success'
    | 'InvalidUsername'
    | 'DuplicateUsername'
    | 'DuplicateEmail'
    | 'ExistingAccount'
    | 'InvalidPassword'
    | 'InvalidExternalRegistrationKey'
    | 'ExpiredExternalRegistrationKey'
    | 'UsedExternalRegistrationKey'
    | 'RequiresTwoFactor'
    | 'LockedOut'
    | 'IpLocked'
    | 'OnHold'
    | 'AccountIncomplete'
    | 'AccountNotIntegrated'
    | 'InvalidCredentials'
    | 'OrganizationDomainMismatch';

export interface OrganizationRegistrationResponse {
    id: string;
    name: string;
    address: {
        address1: string;
        address2: string;
        city: string;
        state: string;
        zipcode: string;
    };
    phoneNumber: string;
    organizationType: {
        id: number;
        description: string;
        isEnabled: boolean;
    };
    website: string;
}

export interface EnrollmentV2RegisterResponse {
    userRegistrationResponse: UserRegistrationResponse;
    organizationRegistrationResponse: OrganizationRegistrationResponse;
    userOrganizationRegistrationResult: string;
}

export interface EnrollmentV2RegisterRequest {
    account: {
        email: string;
        password: string | null;
        firstName: string | undefined;
        lastName: string | undefined;
        captchaResponse: string;
        enrollSourceApplicationId: EnrollSourceApplicationIds;
        externalRegistrationKey?: string;
        codeVerifier?: string;
    };
    organization: {
        name: string;
        address: {
            address1: string;
            address2: string | undefined;
            city: string;
            state: string;
            zipCode: string;
        };
        organizationTypeId: number | undefined;
        phoneNumber: string | undefined;
        webSite: string | undefined;
        fundraisingPurpose: string | null;
        ein?: string;
        countryCode: CountryCode;
        smsOptedIn: boolean;
    };
    blackBoxValue: string | undefined;
    secondaryBlackBoxValue: string | undefined;
}

export const getUserClaimsFromBff = async (): Promise<APIResponse<Claim[]>> => {
    const req = new Request('/bff/user', {
        headers: new Headers({
            'X-CSRF': '1',
        }),
    });

    const response = await fetch(req);

    if (response.ok) {
        const data = await response.json();
        return { data, response };
    }

    return { error: createAPIError(response, null), response };
};

export const enrollmentV2Register = async (
    payload: EnrollmentV2RegisterRequest
): Promise<APIResponse<EnrollmentV2RegisterResponse, EnrollmentV2RegisterResponse>> => {
    const req = new Request('/api/local/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF': '1' },
        body: JSON.stringify(payload),
    });

    const response = await fetch(req);
    const data = await parseResponse(response);

    if (response.ok) {
        return { data, response };
    }

    return {
        error: createAPIError(response, null, data as EnrollmentV2RegisterResponse),
        response,
    };
};
